import React from 'react'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import {
  DATA_TYPE_LABELS,
  PROJECT_DATA_TYPES,
  PROJECT_USAGE_VALUES,
  ProjectDataType,
  ProjectUsage,
  USAGE_LABELS,
} from 'features/projects/constants'
import {
  ARCHIVED_OPTIONS,
  GROUPING_OPTIONS,
  GROUPING_TOOLTIP,
  VISIBILITY_OPTIONS,
} from 'features/projects/features/export/constants'
import {
  ApplyButtonBase,
  Divider,
  ErrorText,
  FilenameDisplay,
  FinePrint,
  FooterRow,
  ModalSubtitle,
  ModalTitle,
  PreparingCopy,
  ProgressBar,
  ProgressTrack,
  Section,
  SectionInfoIcon,
  SectionLabel,
  SuccessBlock,
  SuccessCheckmark,
  ToggleList,
} from 'features/projects/features/export/styles'
import {
  ArchivedFilter,
  ExportFilters,
  ExportStatus,
  GroupingFilter,
  VisibilityFilter,
} from 'features/projects/features/export/types'
import { PressableToggleButton } from 'src/components/ui/PressableToggleButton'
import { PressableToggleButtonGroup } from 'src/components/ui/PressableToggleButtonGroup'

export type ExportModalViewProps = {
  open: boolean
  filters: ExportFilters
  status: ExportStatus
  filename: string | null
  errorMessage: string | null
  onChange: (filters: ExportFilters) => void
  onApply: () => void
  onClose: () => void
}

export const ExportModalView: React.FC<ExportModalViewProps> = ({
  open,
  filters,
  status,
  filename,
  errorMessage,
  onChange,
  onApply,
  onClose,
}) => {
  const toggleSet = <K extends 'data_types' | 'usage'>(
    key: K,
    value: ExportFilters[K][number],
  ): void => {
    const current = filters[key] as string[]
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    onChange({ ...filters, [key]: next })
  }

  const isPreparing = status === 'preparing'
  const isSuccess = status === 'success'
  const isError = status === 'error'

  return (
    <PopoverModal
      isOpen={open}
      title={
        <div>
          <ModalSubtitle>Yogi staff</ModalSubtitle>
          <ModalTitle>Export datasets</ModalTitle>
        </div>
      }
      onClose={onClose}
      width={620}
      disablePointerDismissal={!!isPreparing}
      showClose={!isPreparing}
      bodyPadding="12px 24px"
      footer={
        <FooterRow>
          {isSuccess && filename ? (
            <SuccessBlock>
              <SuccessCheckmark>✓ Downloaded</SuccessCheckmark>
              <FilenameDisplay>{filename}</FilenameDisplay>
            </SuccessBlock>
          ) : isPreparing ? (
            <PreparingCopy>
              <span>Preparing export…</span>
              <ProgressTrack>
                <ProgressBar />
              </ProgressTrack>
              <FinePrint>Larger exports may take a few seconds.</FinePrint>
            </PreparingCopy>
          ) : isError ? (
            <ErrorText>{errorMessage ?? 'Export failed.'}</ErrorText>
          ) : (
            <div />
          )}
          <ApplyButtonBase
            type="button"
            $status={status}
            disabled={isPreparing}
            onClick={isSuccess ? onClose : onApply}
          >
            {isSuccess ? 'Done' : isError ? 'Try again' : 'Export CSV'}
          </ApplyButtonBase>
        </FooterRow>
      }
      content={
        <>
          <Section>
            <SectionLabel>
              Grouping
              <Tooltip content={GROUPING_TOOLTIP}>
                <SectionInfoIcon />
              </Tooltip>
            </SectionLabel>
            <PressableToggleButtonGroup<GroupingFilter>
              options={GROUPING_OPTIONS}
              value={filters.grouping}
              onChange={(v) => onChange({ ...filters, grouping: v })}
            />
          </Section>

          <Divider />

          <Section>
            <SectionLabel>Visibility</SectionLabel>
            <PressableToggleButtonGroup<VisibilityFilter>
              options={VISIBILITY_OPTIONS}
              value={filters.visibility}
              onChange={(v) => onChange({ ...filters, visibility: v })}
            />
          </Section>

          <Divider />

          <Section>
            <SectionLabel>Archived</SectionLabel>
            <PressableToggleButtonGroup<ArchivedFilter>
              options={ARCHIVED_OPTIONS}
              value={filters.archived}
              onChange={(v) => onChange({ ...filters, archived: v })}
            />
          </Section>

          <Divider />

          <Section>
            <SectionLabel>Data types include…</SectionLabel>
            <ToggleList>
              {PROJECT_DATA_TYPES.map((dt) => (
                <PressableToggleButton
                  key={dt}
                  pressed={filters.data_types.includes(dt as ProjectDataType)}
                  onPressedChange={() =>
                    toggleSet('data_types', dt as ProjectDataType)
                  }
                >
                  {DATA_TYPE_LABELS[dt as ProjectDataType]}
                </PressableToggleButton>
              ))}
            </ToggleList>
          </Section>

          <Divider />

          <Section>
            <SectionLabel>Usage</SectionLabel>
            <ToggleList>
              {PROJECT_USAGE_VALUES.map((u) => (
                <PressableToggleButton
                  key={u}
                  pressed={filters.usage.includes(u as ProjectUsage)}
                  onPressedChange={() => toggleSet('usage', u as ProjectUsage)}
                >
                  {USAGE_LABELS[u as ProjectUsage]}
                </PressableToggleButton>
              ))}
            </ToggleList>
          </Section>
        </>
      }
    />
  )
}
